"use client";

import type { WorkspaceFileItem } from "@/app/api/__generated__/models/workspaceFileItem";
import { ErrorCard } from "@/components/molecules/ErrorCard/ErrorCard";
import { useState } from "react";
import type { ArtifactsView } from "../../useArtifactsPage";
import { FileViewerModal } from "../FileViewerModal/FileViewerModal";
import { WorkspaceFolders } from "../WorkspaceFolders/WorkspaceFolders";
import { ArtifactsGrid } from "./ArtifactsGrid";
import { ArtifactsTable } from "./ArtifactsTable/ArtifactsTable";
import type { EmptyStateContent } from "./helpers";
import { LoadMoreSentinel } from "./LoadMoreSentinel";

interface Props {
  files: WorkspaceFileItem[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  emptyState: EmptyStateContent;
  compactEmpty: boolean;
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  listKey: string;
  view: ArtifactsView;
  showFolders: boolean;
  /** Folder whose children the folder rows list; `null` is the root. */
  folderParentId: string | null;
  onSelectFolder: (folderId: string) => void;
}

export function ArtifactsList({
  files,
  isLoading,
  isError,
  error,
  emptyState,
  compactEmpty,
  hasMore,
  isLoadingMore,
  onLoadMore,
  listKey,
  view,
  showFolders,
  folderParentId,
  onSelectFolder,
}: Props) {
  const [openFile, setOpenFile] = useState<WorkspaceFileItem | null>(null);

  if (isError) {
    return (
      <ErrorCard
        context="artifacts"
        responseError={
          error instanceof Error ? { message: error.message } : undefined
        }
      />
    );
  }

  return (
    <>
      {view === "grid" ? (
        <div className="flex flex-col gap-6">
          {showFolders ? (
            <WorkspaceFolders
              parentId={folderParentId}
              onSelectFolder={onSelectFolder}
            />
          ) : null}
          <ArtifactsGrid
            files={files}
            isLoading={isLoading}
            emptyState={emptyState}
            compactEmpty={compactEmpty}
            listKey={listKey}
            onOpen={setOpenFile}
          />
        </div>
      ) : (
        // Keyed so the row selection resets whenever the listing changes.
        <ArtifactsTable
          key={listKey}
          files={files}
          isLoading={isLoading}
          emptyState={emptyState}
          compactEmpty={compactEmpty}
          listKey={listKey}
          showFolders={showFolders}
          folderParentId={folderParentId}
          onSelectFolder={onSelectFolder}
          onOpen={setOpenFile}
        />
      )}
      <LoadMoreSentinel
        hasMore={hasMore}
        isLoading={isLoadingMore}
        onLoadMore={onLoadMore}
        view={view}
      data-sentry-element="LoadMoreSentinel" data-sentry-source-file="ArtifactsList.tsx" />
      {openFile ? (
        <FileViewerModal
          key={openFile.id}
          file={openFile}
          onClose={() => setOpenFile(null)}
        />
      ) : null}
    </>
  );
}
